import React, { FC, FormEvent, useEffect, useRef, useState } from "react";
import OrderDocuments from "./OrderDocuments";
import OrderNotes from "./OrderNotes";
import OrderHistory from "./OrderHistory";
import IconButton from "../../../components/common/form/IconButton";
import { hasPermission } from "../../../helpers/utils/AccessControlUtils";
import {
    useCancelOrderMutation,
    useLazyGetOrderListByCustomerQuery,
    useMarkAsIssueOrderMutation,
    useMarkAsRushOrderMutation,
    useUpdateCustomerHoldMutation,
    useUpdateMarkAsCustomerRetrievedMutation,
    useUpdateUnLockedProductsFromCustomerMutation
} from "../productsApi";
import { skipToken } from "@reduxjs/toolkit/query";
import ConfirmationModal from "../../../components/layout/modal/ConfirmationModal";
import AssignmentLogs from "./AssignmentLogs";
import OrderPricingDetail from "./OrderPricingDetail";
import { useNavigate, useParams } from "react-router-dom";
import { useGetCustomerByIdQuery } from "../../customer/customerApi";
import { useGetBranchByIdQuery } from "../../customer/branchApi";
import OrderToDoList from "./OrderToDoList";
import ProductsDetail from "./ProductsDetail";
import OrderDetailInfo from "./OrderDetailInfo";
import OrderProperties from "./OrderProperties";
import SkeletonCell from "../../../components/common/SkeletonCell";
import { useUtilDataQuery } from "../../profile/profileApi";
import ButtonTray from "../../../components/common/form/ButtonTray";
import { FaLockOpen, FaPause, FaPlay } from "react-icons/fa";
import { FaLock } from "react-icons/fa6";
import UpdateOrderStatusModal from "../modal/UpdateOrderStatusModal";
import IncidentHistory from "./IncidentHistory";

interface props {
    orderQuery: { data: any, isLoading: boolean, isError: boolean } | any,
}

const ProductTabSection: FC<props> = ({ orderQuery }) => {

    let { orderId } = useParams();

    const [selectedTab, setSelectedTab] = useState<any>(null);
    const [isLoading, setIsLoading] = useState<any>(false);
    const [getOrderListByCustomerQuery, { data: getOrderListByCustomerData }] = useLazyGetOrderListByCustomerQuery();
    const [customerHold, updateCustomerHold] = useUpdateCustomerHoldMutation();
    const [markAsCustomerRetrievedApi, markAsCustomerRetrievedResponse] = useUpdateMarkAsCustomerRetrievedMutation();
    const [updateUnLockedProductsFromCustomerApi, updateUnLockedProductsFromCustomerResponse] = useUpdateUnLockedProductsFromCustomerMutation();
    const navigate = useNavigate();
    const [cancelOrder, cancelOrderApiResponse] = useCancelOrderMutation();
    const [markAsIssue] = useMarkAsIssueOrderMutation();
    const [markAsRush] = useMarkAsRushOrderMutation();
    const { data: utilData } = useUtilDataQuery("");
    const [showDropdown, setShowDropdown] = useState<boolean>(false)
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [showUdpateStatusModal, setShowUpdateStatusModal] = useState(false);
    const [showMarkAsIssueStatusModal, setShowMarkAsIssueModal] = useState(false);
    const [showMarkAsRushStatusModal, setShowMarkAsRushModal] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const {
        data: customerData,
    } = useGetCustomerByIdQuery(orderQuery?.data?.customerId || skipToken);

    const {
        data: branchData,
    } = useGetBranchByIdQuery(orderQuery?.data?.branchId ? {
        branchId: orderQuery?.data?.branchId
    } : skipToken);

    useEffect(() => {
        const hash = window.location.hash.replace("#", "");
        if (hash) {
            setSelectedTab(hash);
        }
    }, []);

    useEffect(() => {

        if (orderQuery?.data) {
            getOrderListByCustomerQuery({
                customerId: orderQuery.data.customerId,
                customerOrderNumber: orderQuery.data.customerOrderNumber,
            })
        }

    }, [orderQuery?.data]);


    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setShowDropdown(false);
            }
        }

        if (showDropdown) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [showDropdown]);

    // Update the URL hash when the selectedTab changes
    useEffect(() => {
        if (selectedTab) {
            window.location.hash = selectedTab;
        }
    }, [selectedTab]);

    const handleCancelOrder = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            await cancelOrder(orderId).unwrap();
        } catch (error) {
            console.error("Error cancelling order:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleMarkAsIssue = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            await markAsIssue(orderId).unwrap();
        } catch (err) {
            console.error("Error marking as issue:", err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleMarkAsRush = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            await markAsRush(orderId).unwrap();
        } catch (error) {
            console.error("Error marking as rush:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCustomerHoldSubmit = async (e: any) => {
        e.preventDefault();
        if (orderId) {
            await customerHold(orderId).unwrap();
        }
    }

    const handleMarkAsRetrieved = async (e: any) => {
        e.preventDefault();
        if (orderId) {
            await markAsCustomerRetrievedApi(orderId).unwrap();
        }
    }

    const handleUpdateLockedProductsToCustomer = async (e: any) => {
        e.preventDefault();
        let productIds = orderQuery?.data?.products.map((el: any) => el?.id);
        if (productIds.length > 0) {
            await updateUnLockedProductsFromCustomerApi(productIds).unwrap();
        }
    }

    return (
        <>
            <div className={`md:grid sm:block grid-cols-12 gap-3`}>
                <div className={"col-span-5 py-2"}>
                    {!getOrderListByCustomerData ? <SkeletonCell /> :
                        <select className={"border border-text-3 rounded p-2"} value={orderId}
                            onChange={(e) => window.location.href = "/orders/" + e.target.value}>
                            {getOrderListByCustomerData.map((curOrder: any) => {
                                return (<option value={curOrder.id}>{curOrder.tgxId}</option>)
                            })}
                        </select>
                    }
                </div>

                <div className={`col-span-7 flex justify-end gap-2 py-2`}>
                    <ButtonTray
                        buttons={
                            [
                                {
                                    buttonProps: {

                                        modal: <ConfirmationModal
                                            title={"Mark as Retrieved"}
                                            bodyMessage={"Are you sure you want to mark this order as Retrieved?"}
                                            onSubmit={handleMarkAsRetrieved}
                                            show={true}
                                            setShow={() => {
                                            }}
                                            isLoading={markAsCustomerRetrievedResponse.isLoading} />,
                                        triggerButton:
                                            <IconButton
                                                className="!bg-highlight-0 !text-text-0 p-1 pl-3 pr-3 rounded-lg"
                                                label="Mark 'Retrieved'"
                                            />,
                                        isVisible: utilData?.customerLevel === 'CUSTOMER' && hasPermission(["CUSRET-V"]) && orderQuery.data?.customerRetrieved === false,
                                    },

                                    buttonType: "modal"
                                },
                                {
                                    buttonProps: {

                                        modal: <ConfirmationModal
                                            title={!orderQuery?.data?.customerHold ? "Put on Hold" : "Remove from Hold"}
                                            bodyMessage={!orderQuery?.data?.customerHold ? "Are you sure you want to Put this order on Hold?" : "Are you sure you want to Remove this order from Hold?"}
                                            onSubmit={handleCustomerHoldSubmit}
                                            show={true}
                                            setShow={() => {
                                            }}
                                            isLoading={updateCustomerHold.isLoading} />,
                                        triggerButton:
                                            <IconButton
                                                icon={!orderQuery?.data?.customerHold ? <FaPause /> : <FaPlay />}
                                                className="!bg-highlight-0 !text-text-0 p-1 pl-3 pr-3 rounded-lg"
                                                onClick={() => ''}
                                            />,
                                        isVisible: hasPermission(["CUSHOLD-U"]),

                                    },

                                    buttonType: "modal"
                                },
                                {
                                    buttonProps: {

                                        modal: <ConfirmationModal
                                            title={"Escalate To Tenant"}
                                            bodyMessage={"Are you sure you want to escalate this order? This order will not be discounted if you do so."}
                                            onSubmit={handleUpdateLockedProductsToCustomer}
                                            show={true}
                                            setShow={() => {
                                            }}
                                            isLoading={updateUnLockedProductsFromCustomerResponse.isLoading} />,
                                        triggerButton:
                                            <>
                                                <IconButton
                                                    className={`!bg-text-3 p-1 pl-3 pr-3 rounded-lg ${orderQuery.data && orderQuery.data?.products?.filter((el: any) => el.lockedToCustomer === true).length > 0 ? "" : "!cursor-default"}`}
                                                    label={'Escalate To Tenant'}
                                                    onClick={() => {
                                                        if (orderQuery.data && orderQuery.data?.products?.filter((el: any) => el.lockedToCustomer === true).length > 0) {

                                                        }
                                                    }}
                                                />
                                                <IconButton
                                                    icon={orderQuery.data && orderQuery.data?.products?.filter((el: any) => el.lockedToCustomer === true).length > 0 ?
                                                        <FaLock /> : <FaLockOpen />}
                                                    className={`!bg-text-3 !text-text-0 p-1 pl-3 pr-3 rounded-lg ${orderQuery.data && orderQuery.data?.products?.filter((el: any) => el.lockedToCustomer === true).length > 0 ? "" : "!cursor-default"}`}
                                                    onClick={() => ''}
                                                />
                                            </>,
                                        isVisible: utilData?.customerLevel !== 'CUSTOMER' || orderQuery.data?.customerDTO?.fulfillmentCustomer === true,

                                    },

                                    buttonType: "modal"
                                },
                            ]}
                        align={"end"}
                        gap={3}
                        addTopLine={false}
                    />
                    <div ref={dropdownRef} className={"cursor relative"}>
                        <div className={
                            "flex items-center gap-3 text-text-0 bg-[rgb(70,130,180)] border border-transparent rounded-[11px] px-[14px] py-[6px] ml-0 cursor"
                        }>
                            <button
                                className="text_color"
                                onClick={() => {
                                    setShowUpdateStatusModal(true)
                                }}
                                aria-hidden="true"
                            >
                                Update Statuses
                            </button>
                            <span
                                className="inline-block border-l pl-1 items-baseline cursor-pointer border-surface-0 justify-center"
                                onClick={() => {
                                    setShowDropdown(!showDropdown);
                                }}>
                                <i className="fa fa-sort-desc relative bottom-1 ml-1" aria-hidden="true"></i>
                            </span>
                        </div>
                        {showDropdown && (
                            <div
                                className="cursor dropdown dropdown-button-opener absolute right-[10px] bg-text-0 text-left z-[9] top-[39px] w-48 border rounded border-text-1">
                                <div className="dropdown-menu">
                                    {hasPermission(["CANCELORDR-V", "CANCELORDR-U"], true) &&
                                        <a
                                            className="dropdown-item p-2 block border-b border-text-1 hover:bg-surface-6 hover:text-text-0 cursor-pointer"
                                            onClick={() => {
                                                setShowCancelModal(true)
                                            }}
                                        >
                                            Cancel
                                        </a>
                                    }
                                    {hasPermission(["MARKASISSUE-V", "MARKASISSUE-U"], true) &&
                                        <a
                                            className="dropdown-item p-2 block border-b border-text-1 hover:bg-surface-6 hover:text-text-0 cursor-pointer"
                                            onClick={() => {
                                                setShowMarkAsIssueModal(true)
                                            }}
                                        >
                                            Mark as Issue
                                        </a>
                                    }
                                    {hasPermission(["MARKASRUSH-V", "MARKASRUSH-U"], true) && orderQuery?.data.rushDate == null &&
                                        <a
                                            className="dropdown-item p-2 block border-b border-text-1 hover:bg-surface-6 hover:text-text-0 cursor-pointer"
                                            onClick={() => {
                                                setShowMarkAsRushModal(true)
                                            }}
                                        >
                                            Mark as Rush
                                        </a>
                                    }
                                </div>

                            </div>
                        )}
                    </div>
             

                    {orderQuery?.data && hasPermission(["ORDR-U"]) && <IconButton
                        className="!bg-highlight-0 !text-text-0 p-1 pl-3 pr-3 rounded-lg"
                        label="Re-order"
                        onClick={() => {
                            navigate(`/orders/re-order/${orderId}`,
                                {
                                    state: {
                                        orderNumber: orderQuery?.data?.customerOrderNumber,
                                        customerId: {
                                            value: orderQuery?.data?.customerId,
                                            label: customerData?.customerName
                                        },
                                        branchId: {
                                            value: orderQuery?.data?.branchId,
                                            label: branchData?.displayLabel || branchData?.name
                                        }
                                    }
                                }
                            )
                        }}
                    />}

                </div>
            </div>

            <div className="md:grid sm:block grid-cols-12 gap-5">
                <div className="col-span-6">
                    <OrderDetailInfo orderQuery={orderQuery} />
                    {hasPermission(["PRCINFO-V"]) &&
                        <OrderPricingDetail orderData={orderQuery} />}
                    <OrderProperties orderData={orderQuery} />
                </div>

                <div className="col-span-6 mb-5">
                    {<ProductsDetail />}
                    {hasPermission(["ONOT-V", "ONOT-R"], true) && <OrderNotes productId={selectedTab} />}
                    {hasPermission(["TODO-V", "TODO-R"], true) && <OrderToDoList productId={selectedTab} />}
                    {hasPermission(["FILE-V", "FILE-R"], true) &&
                        <OrderDocuments productId={selectedTab} orderQuery={orderQuery} />}
                    {hasPermission(["OLOG-V", "OLOG-R"], true) && <OrderHistory productId={selectedTab} />}

                    {utilData?.customerLevel !== 'CUSTOMER' && hasPermission(["ASSLOG-V"]) && <AssignmentLogs productId={selectedTab} />}
                    {hasPermission(["INCR-V", "INCR-R"], true) && <IncidentHistory orderId={orderId} />}

                </div>
            </div>
            {showUdpateStatusModal &&
                <UpdateOrderStatusModal
                    show={showUdpateStatusModal}
                    setShow={setShowUpdateStatusModal}
                    orderData={orderQuery?.data}
                />
            }
            {showCancelModal && (
                <ConfirmationModal
                    show={showCancelModal}
                    setShow={setShowCancelModal}
                    title={"Cancel Order"}
                    bodyMessage={"Are you sure you want to cancel this order?"}
                    onSubmit={handleCancelOrder}
                    isLoading={isLoading}
                />
            )}

            {showMarkAsIssueStatusModal &&
                <ConfirmationModal
                    show={showMarkAsIssueStatusModal}
                    setShow={setShowMarkAsIssueModal}
                    title={"Mark as Issue"}
                    bodyMessage={"Are you sure you want to mark this order as an issue?"}
                    onSubmit={handleMarkAsIssue}
                    isLoading={isLoading}
                />
            }
            {showMarkAsRushStatusModal &&
                <ConfirmationModal
                    show={showMarkAsRushStatusModal}
                    setShow={setShowMarkAsRushModal}
                    title={"Mark as Rush"}
                    bodyMessage={"Are you sure you want to mark this order as an rush?"}
                    onSubmit={handleMarkAsRush}
                    isLoading={isLoading}
                />
            }
        </>
    )
}
export default ProductTabSection